import React, { useState, HTMLAttributes, forwardRef } from 'react';
import { Icon } from '../Icon';
import '../../base.scss';
import './Button.scss';
import { classNames } from '../../utilities';

export interface ButtonProps extends HTMLAttributes<HTMLElement> {
  /** required for accessibility on icon-only buttons */
  'aria-label'?: string;
  /** The content to display inside the button */
  children?: string | string[] | React.ReactNode;
  /** The content to display inside the button when processing a click event */
  childrenOnClick?: string | string[] | React.ReactNode;
  /** Attach a custom class to the button */
  className?: string;
  /** stable key for tests or 3rd party handlers */
  'data-testid'?: string;
  /**
   * button is not clickable
   * @default false
   */
  disabled?: boolean;
  /** Convert the button to a link.  An 'a' tag will be rendered. */
  href?: string;
  /** Buttons can contain a single svg icon */
  icon?: string;
  /** Icon position in relation to inner text */
  iconPosition?: 'left' | 'right';
  /** Specify an ID for the button */
  id?: string;
  /**
   * Disabled + spinner
   * @default false
   */
  isLoading?: boolean;
  /**
   * 2 size options
   * @default default
   */
  size?: 'default' | 'lg';
  /** Use with href for a-tag target: _blank|_self|_parent|_top|framename */
  target?: string;
  /** Callback when clicked */
  onClick?(): Promise<void> | void;
  /**
   * Support multiple button styles
   * @default primary
   */
  variant?: 'primary' | 'secondary' | 'danger' | 'icon-only' | 'icon-link' | 'link';
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props: ButtonProps, componentRef) => {
  const {
    children,
    childrenOnClick,
    className,
    onClick,
    icon,
    href,
    target,
    iconPosition = 'left',
    disabled = false,
    isLoading = false,
    size = 'default',
    variant = 'primary',
    ...rest
  } = props;

  const [awaitingClickEvent, toggleClickEvent] = useState(false);

  const isLink = !!href;

  if ((variant === 'icon-only' || children === null) && !props['aria-label']) {
    throw new Error('Icon Buttons require aria-label for accessibility');
  }

  if (variant === 'link' && icon) {
    throw new Error('Use icon-link variant instead');
  }

  if (variant === 'icon-link' && !icon) {
    throw new Error('Use link variant instead');
  }


  if (target && !isLink) {
    throw new Error('Target is only applied to <a> tags with the href prop');
  }

  if (isLink && disabled) {
    throw new Error('<a> tags do not support the disabled attribute');
  }

  // if awaiting an async click event then disable the button, otherwise use the prop
  let isDisabled = disabled;
  let childResult = children;
  if (awaitingClickEvent) {
    isDisabled = awaitingClickEvent;
    if (childrenOnClick) {
      childResult = childrenOnClick;
    }
  }

  const buttonClassNames = classNames(
    'ps-button',
    `ps-button--${variant}`,
    size === 'lg' ? 'ps-button--large' : '',
    isDisabled ? 'ps-button--disabled' : '',
    icon && variant !== 'icon-only' && iconPosition === 'left' ? 'ps-button--icon-left' : '',
    icon && variant !== 'icon-only' && iconPosition === 'right' ? 'ps-button--icon-right' : '',
    className
  );

  const innerText = children ? <span>{childResult}</span> : null;

  // TODO: discuss how to handle wrapper prop for Icon e.g. circle icons
  let content = (
    <>
      {icon && !isLoading && iconPosition === 'left' && <Icon name={icon} />}
      {innerText}
      {icon && !isLoading && iconPosition === 'right' && <Icon name={icon} />}
    </>
  );

  if (icon && variant === 'icon-only') {
    content = <Icon name={icon} />;
  }

  if (isLink) {
    return (
      <a
        ref={componentRef as React.RefObject<HTMLAnchorElement>}
        href={href}
        target={target}
        className={buttonClassNames}
        aria-label={props['aria-label']}
        onClick={() => {
          if (onClick) {
            const clickPromise = onClick();
            toggleClickEvent(true);
            if (clickPromise) {
              clickPromise.finally(() => {
                toggleClickEvent(false);
              });
            } else {
              toggleClickEvent(false);
            }
            return clickPromise;
          }
        }}
        {...rest}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={componentRef as React.RefObject<HTMLButtonElement>}
      type="button"
      aria-label={props['aria-label']}
      className={buttonClassNames}
      disabled={isDisabled}
      onClick={() => {
        if (onClick) {
          const clickPromise = onClick();
          toggleClickEvent(true);
          if (clickPromise) {
            clickPromise.finally(() => {
              toggleClickEvent(false);
            });
          } else {
            toggleClickEvent(false);
          }
          return clickPromise;
        }
      }}
      {...rest}
    >
      {content}
    </button>
  );
});

Button.displayName = 'Button';
export { Button };
